<template>
  <PopUp :show="modal" @hideModal="hideModal">
    <ValidationObserver
      v-slot="{ invalid }"
      tag="div"
      ref="observer"
      class="event"
      id="scroll-block"
    >
      <header class="event__header">
        <h3 class="event__header_title">{{ event.id ? "Edit" : "Add" }} Event</h3>
        <div class="event__header_actions">
          <BaseButton
            v-if="item"
            class="button--sm button--outline button--uppercase"
            text="Delete Event"
            @click="$emit('removeEvent', event)"
          ></BaseButton>
          <BaseButton
            class="button--sm button--green button--uppercase"
            text="Save Changes"
            :loading="changesLoading"
            :disabled="isSaveEventDisabled"
            @click="saveEvent"
          ></BaseButton>
        </div>
      </header>
      <section class="form">
        <div v-if="event.status" class="form__header">
          <SimpleToggle
            placeholder="Featured"
            :disabled="isToggleDisabled"
            v-model="event.featured"
            :selected="+event.featured"
            :items="STATUS_LIST"
            @input="event.featured = $event"
          ></SimpleToggle>
          <p v-if="!event.status" class="form__header_notification">
            Please make this event Enabled before using this option
          </p>
          <p v-if="!event.home_page_image" class="form__header_notification">
            Please add a homepage image before using this option
          </p>
        </div>
        <div class="form__header">
          <SimpleToggle
            placeholder="Private Event"
            :disabled="invalid"
            v-model="event.is_private"
            :selected="+event.is_private"
            :items="[
              { id: '1', title: 'Active' },
              { id: '0', title: 'Inactive' },
            ]"
            @input="event.is_private = $event"
          ></SimpleToggle>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Event Information</h3>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ required: true, min: MIN_ON_TEXT_INPUT, max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Title"
              :required="true"
              :validateError="errors[0]"
              v-model="event.title"
              :element="event.title"
              @input="event.title = $event"
            ></BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <div class="form__row_col form__row_col--side">
            <Toggle
              placeholder="Active Toggle"
              v-model="event.status"
              :disabled="invalid || event.search_image === ''"
              :selected="+event.status"
              :items="STATUS_LIST"
              @input="event.status = $event"
            ></Toggle>
          </div>
          <div class="form__row_col form__row_col--side">
            <Toggle
              placeholder="VIP Event"
              v-model="event.vip"
              :selected="+event.vip"
              :items="[
                { id: '1', title: 'Yes' },
                { id: '0', title: 'No' },
              ]"
              @input="event.vip = $event"
            ></Toggle>
          </div>
        </div>
        <div class="form__row form__row--align-start">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true }"
          >
            <BaseSelect
              placeholder="Location"
              v-model="event.location"
              :selected="event.location"
              :items="event_types"
              :required="true"
              @input="event.location = $event.id"
            ></BaseSelect>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, path: true, max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Path"
              :required="true"
              :validateError="errors[0] || serverError.slug"
              v-model="event.slug"
              :element="event.slug"
              @input="event.slug = $event"
            >
              <template #text>
                <span class="form-control__simple-text"
                  >Example: {{ `${url}/events/${event.slug || "path"}` }}</span
                ></template
              >
            </BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Address"
              :validateError="errors[0]"
              v-model="event.address"
              :element="event.address"
              @input="event.address = $event"
            >
            </BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <div class="form__row_col form__row_col--side">
            <DatePicker
              placeholder="Start Date"
              :required="true"
              :validateError="serverError.start_date"
              :value="event.start_date"
              v-model="event.start_date"
              @input="event.start_date = $event"
              :disabled="isDatePickerDisabled"
            ></DatePicker>
          </div>
          <div class="form__row_col form__row_col--side">
            <DatePicker
              placeholder="End Date"
              :required="true"
              :validateError="serverError.end_date"
              :startDate="event.start_date"
              :value="event.end_date"
              v-model="event.end_date"
              @input="event.end_date = $event"
              :disabled="isDatePickerDisabled"
            ></DatePicker>
          </div>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ min: MIN_ON_TEXT_INPUT, max: MAX_TEXT_LENGTH }"
            v-slot="{ errors }"
          >
            <Editor
              placeholder="Dates & Hours Description"
              :validateError="errors[0]"
              :value="event.schedule"
              @input="event.schedule = $event"
              v-model="event.schedule"
              :toolbar="['bold', 'italic']"
            ></Editor>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ required: true }"
            v-slot="{ errors }"
          >
            <BaseNumberInput
              placeholder="Number of guests limit"
              :required="true"
              v-model="event.guests_limit"
              :element="event.guests_limit"
              @input="event.guests_limit = $event"
            ></BaseNumberInput>
          </ValidationProvider>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Event Description</h3>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ required: true, max: MAX_DESCRIPTION_LENGTH }"
            v-slot="{ errors }"
          >
            <BaseTextarea
              placeholder="Description"
              :required="true"
              :validateError="errors[0]"
              v-model="event.description"
              :element="event.description"
              @input="event.description = $event"
            ></BaseTextarea>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Button Text"
              :validateError="errors[0]"
              v-model="event.button_text"
              :element="event.button_text"
              @input="event.button_text = $event"
            >
            </BaseInput>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Button URL"
              :validateError="errors[0]"
              v-model="event.button_url"
              :element="event.button_url"
              @input="event.button_url = $event"
            >
            </BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="RSVP Button URL"
              :validateError="errors[0]"
              v-model="event.button_rsvp_url"
              :element="event.button_rsvp_url"
              @input="event.button_rsvp_url = $event"
            >
            </BaseInput>
          </ValidationProvider>
          <div class="form__row_col form__row_col--side">
            <Toggle
              placeholder="Safety Protocol"
              v-model="event.safety_protocol"
              :selected="+event.safety_protocol"
              :items="STATUS_LIST"
              @input="event.safety_protocol = $event"
            ></Toggle>
          </div>
        </div>
        <div class="form__row" v-if="+event.safety_protocol === 1">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{
              required: true,
              min: MIN_ON_TEXT_INPUT,
              max: MAX_DESCRIPTION_LENGTH,
            }"
            v-slot="{ errors }"
          >
            <Editor
              placeholder="Safety Protocol Information"
              :required="true"
              :validateError="errors[0]"
              :value="event.safety_protocol_info"
              @input="event.safety_protocol_info = $event"
              v-model="event.safety_protocol_info"
              :toolbar="[
                'heading',
                'bold',
                'italic',
                'underline',
                'link',
                'blockQuote',
                '|',
                'alignment',
                'numberedList',
                'bulletedList',
              ]"
            ></Editor>
          </ValidationProvider>
        </div>
        <div class="form__row form__row--align-start">
          <div class="form__row_col">
            <BaseSelect
              placeholder="Associated Exhibition"
              v-model="event.exhibition_id"
              :selected="event.exhibition_id"
              :items="exhibitionsList"
              :close="true"
              @input="event.exhibition_id = $event.id"
            ></BaseSelect>
          </div>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Event Images</h3>
        <div class="form__row">
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label"
              >Search Image (recommended size: 1500×1296px)<span class="form__row_label_required"
                >*</span
              ></span
            >
            <DragAndDropImage
              class="uploader--full-width"
              id="search"
              size="15"
              :image="event.search_image"
              :v-model="event.search_image"
              @input="
                event.search_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                event.search_image = '';
              "
            ></DragAndDropImage>
          </div>
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label">Header Image (recommended size: 2560×780px) </span>
            <DragAndDropImage
              class="uploader--full-width"
              id="header"
              size="15"
              :image="event.header_image"
              :v-model="event.header_image"
              @input="
                event.header_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                event.header_image = '';
              "
            ></DragAndDropImage>
          </div>
        </div>
        <div class="form__row form__row--bordered">
          <span class="form__row_label">
            Homepage Image (recommended size: 2560×1440px)
            <span v-if="event.featured" class="form__row_label_required">*</span>
          </span>
          <DragAndDropImage
            class="uploader--full-width"
            id="home"
            size="15"
            :image="event.home_page_image"
            :v-model="event.home_page_image"
            @input="
              event.home_page_image = $event.image;
              $event.removed ? removedImages.push($event.removed) : false;
            "
            @removeImage="
              removedImages.push($event);
              event.home_page_image = '';
            "
          ></DragAndDropImage>
        </div>
      </section>
      <section class="form" v-for="(sponsor, id) in event.sponsors" :key="id">
        <h3 class="form__header">
          Sponsor {{ id + 1 }}
          <IconButton
            class="event__sponsor-button button--admin button--xsm"
            text="Remove Sponsor"
            @click="removeSponsor(id)"
          >
            <IconClose width="9" height="9"></IconClose>
          </IconButton>
        </h3>
        <div class="form__row form__row--align-start">
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label">Sponsor Image (recommended size: 468×468px)</span>
            <DragAndDropImage
              class="uploader--full-width"
              :id="`sponsor-${id}`"
              size="15"
              :canRemove="false"
              :image="sponsor.image"
              :v-model="sponsor.image"
              @input="
                sponsor.image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                sponsor.image = '';
              "
            ></DragAndDropImage>
            <BaseInput
              class="form-control--xs"
              style="margin-top: 20px"
              placeholder="Caption"
              v-model="sponsor.caption"
              :element="sponsor.caption"
              @input="sponsor.caption = $event"
            ></BaseInput>
          </div>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Sponsor URL"
              :validateError="errors[0]"
              v-model="sponsor.url"
              :element="sponsor.url"
              @input="sponsor.url = $event"
            >
            </BaseInput>
          </ValidationProvider>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">
          Sponsor
          <IconButton
            class="event__sponsor-button button--admin button--xsm"
            text="Add Sponsor"
            @click="addSponsor(sponsor)"
          >
            <IconPlus width="12" height="12"></IconPlus>
          </IconButton>
        </h3>
        <div class="form__row form__row--align-start">
          <div
            class="form__row_col form__row_col--side form__row_col--bordered"
            :class="{ 'form__row_col--error': sponsor.error }"
          >
            <span class="form__row_label">Sponsor Image</span>
            <DragAndDropImage
              class="uploader--full-width"
              id="add-sponsor"
              size="15"
              :image="sponsor.image"
              :v-model="sponsor.image"
              @input="
                sponsor.image = $event.image;
                sponsor.error = false;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                sponsor.image = '';
              "
            >
            </DragAndDropImage>
          </div>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Sponsor URL"
              :validateError="errors[0]"
              v-model="sponsor.url"
              :element="sponsor.url"
              @input="sponsor.url = $event"
            >
            </BaseInput>
          </ValidationProvider>
          <span v-if="sponsor.error" class="form__row_text form__row_text--error">
            This field is required
          </span>
        </div>
      </section>
      <section v-if="!item?.id" class="form">
        <h3 class="form__header">Add Timeslots</h3>
        <p class="form__header_notification">
          You can add a time slot after saving your changes. Please save first to enable this
          option.
        </p>
      </section>
      <section v-else>
        <AddTimelog
          v-if="item && item?.id"
          :id="item.id"
          :start_date="item.start_date"
          :end_date="item.end_date"
          :timeslotAvailability="timeslotAvailability"
          :serverError="serverError"
          :isExhibition="false"
        />
        <Timeslots
          v-if="itemTimeslots.length > 0"
          :id="item.id"
          :items="itemTimeslots"
          :startDate="item.start_date"
          :endDate="item.end_date"
          :list="list"
          :headerItems="headerItems"
          :possibleTimes="possibleTimes"
          :item="rsvp"
          :serverError="serverError"
          :changesLoading="changesLoading"
          :itemTimeslots="itemTimeslots"
          :isExhibition="false"
        ></Timeslots>
      </section>
    </ValidationObserver>
  </PopUp>
</template>
<script>
import PopUp from "@/components/PopUp.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import BaseButton from "@/components/buttons/BaseButton.vue";
import BaseInput from "@/components/inputs/BaseInput.vue";
import BaseNumberInput from "@/components/inputs/BaseNumberInput.vue";
import BaseSelect from "@/components/inputs/BaseSelect.vue";
import BaseTextarea from "@/components/inputs/BaseTextarea.vue";
import DragAndDropImage from "@/components/DragAndDropImage.vue";
import Editor from "@/components/inputs/Editor.vue";
import { mapGetters, mapMutations } from "vuex";
import Toggle from "@/components/inputs/Toggle.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import SimpleToggle from "@/components/inputs/SimpleToggle.vue";

import Timeslots from "../components/Timeslots.vue";
import AddTimelog from "../components/AddTimelog.vue";

import IconButton from "@/components/buttons/IconButton.vue";
import IconPlus from "@/components/icons/IconPlus.vue";
import IconClose from "@/components/icons/IconClose.vue";
import {
  MIN_ON_TEXT_INPUT,
  MAX_ON_TEXT_INPUT,
  MAX_TEXT_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  STATUS_LIST,
} from "@/constants";

export default {
  props: { item: { type: Object, default: null }, modal: { type: Boolean, default: false } },
  components: {
    PopUp,
    ValidationObserver,
    ValidationProvider,
    BaseButton,
    BaseInput,
    BaseNumberInput,
    BaseTextarea,
    BaseSelect,
    DragAndDropImage,
    Editor,
    Toggle,
    DatePicker,
    IconButton,
    IconPlus,
    IconClose,
    SimpleToggle,
    Timeslots,
    AddTimelog,
  },
  data() {
    return {
      url: process.env.VUE_APP_FRONTEND_URL,
      event: {
        title: "",
        status: 0,
        vip: "0",
        location: "",
        slug: "",
        address: "",
        start_date: "",
        end_date: "",
        schedule: "",
        guests_limit: 0,
        description: "",
        button_text: "",
        button_url: "",
        button_rsvp_url: "",
        safety_protocol: "0",
        safety_protocol_info: "",
        exhibition_id: "",
        search_image: "",
        header_image: "",
        home_page_image: "",
        featured: false,
        sponsors: [],
        is_private: false,
      },
      sponsor: {
        image: "",
        url: "",
        error: false,
        text: "This field is required",
      },
      removedImages: [],
      changed: false,
      MIN_ON_TEXT_INPUT,
      MAX_ON_TEXT_INPUT,
      MAX_TEXT_LENGTH,
      MAX_DESCRIPTION_LENGTH,
      STATUS_LIST,
    };
  },
  created() {
    if (this.item != null) {
      this.event = JSON.parse(JSON.stringify(this.item));
    }
  },
  beforeDestroy() {
    this.setError(null);
  },
  computed: {
    ...mapGetters("general", {
      event_types: "getExhibitionTypes",
    }),
    ...mapGetters("exhibitions", {
      exhibitionsList: "getItemsList",
      headerItems: "getRsvpListHeaders",
    }),
    ...mapGetters("events", {
      serverError: "getServerError",
      changesLoading: "getChangesLoading",
      list: "getRsvpList",
      possibleTimes: "getPossibleTimes",
      rsvp: "getRsvp",
      timeslotAvailability: "getTimeslotAvailability",
      serverError: "getServerError",
      itemTimeslots: "getItemTimeslots",
    }),
    isEventIncomplete() {
      return (
        this.invalid ||
        this.event.search_image === "" ||
        (this.event.featured && this.event.home_page_image === "")
      );
    },
    isSaveEventDisabled() {
      return this.isEventIncomplete || !this.changed;
    },
    isToggleDisabled() {
      return this.isEventIncomplete || !this.event.status;
    },
    isDatePickerDisabled() {
      return this.event.id && this.event.timeslots?.length > 0;
    },
  },
  watch: {
    event: {
      handler: function (val, oldVal) {
        if (this.item != null) {
          if (this.$refs.observer.flags.dirty) {
            this.changed = true;
          }
          if (!val.status) {
            this.event.featured = false;
          }
        } else {
          this.changed = true;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations("events", ["setError"]),
    hideModal() {
      if (this.changed) {
        this.$swal({
          html: "<h2>Are you sure you want to exit <br/> without saving?</h2>",
          showCancelButton: true,
          cancelButtonText: "Exit without saving",
          confirmButtonText: "Save changes",
        }).then(result => {
          if (result.value) {
            this.$refs.observer.flags.valid &&
            this.event.search_image != "" &&
            this.event.home_page_image != ""
              ? this.saveEvent()
              : this.showToast();
          } else this.$emit("hideModal");
        });
      } else this.$emit("hideModal");
    },
    showToast() {
      this.$toasted.show(`Please fill in all required fields`, {
        duration: 2000,
        type: "error",
      });
    },
    addSponsor({ image, url }) {
      if (image === "") {
        this.sponsor.error = true;
      } else {
        this.event.sponsors.push({ image, url });
        this.sponsor = {
          ...this.sponsor,
          image: "",
          url: "",
        };
      }
    },
    removeSponsor(id) {
      this.event.sponsors.splice(id, 1);
    },
    saveEvent() {
      let data = {
        ...this.event,
      };
      if (this.event.id) {
        this.$emit("updateEvent", { data: data, imageForRemove: this.removedImages });
      } else {
        this.$emit("createEvent", { data: data, imageForRemove: this.removedImages });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.event {
  @include scrollableContainer;
  &__header {
    @include responsiveHeaderLayout;
    &_title {
      @include titleBase();
    }
    &_actions {
      @include actionToolbar;
    }
  }
  &__sponsor-button {
    @include sponsorButton;
  }
}
</style>

